import React from 'react';
import { connect } from 'react-redux';
import { isNil, isEmpty, findIndex, indexOf, filter, map, uniqBy, get } from 'lodash';
import serviceContainer from 'src/ServiceContainer';

import container from 'src/ServiceContainer';
import { AppState, AppThunkDispatch } from 'src/store';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { makePopoverSensitive } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { FlowSheetGrid } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetGrid';
import { ComponentTypeGridDispatchProps } from 'src/pages/AssortmentBuild/Pricing/PricingGrid';

import { FlowSheetGridProps } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheet.types';
import { ASSORTMENT, ASSORTMENT_BUILD_FILTER_WARNING } from 'src/utils/Domain/Constants';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { TreePivot } from 'src/worker/pivotWorker.types';

import {
  fetchWorklistOverTimeData,
  receiveFlowSheetByStyleConfig,
  submitMassEditPayload as submitFlowSheetPayload,
  fetchMassEditData,
  receiveSomeFlowSheetConfig,
  FlowSheetViewDefns,
} from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetByStyle.slice';
import { updateAssortmentPlan } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.actions';
import Subheader from 'src/components/Subheader/Subheader.container';
import { LegacySubheaderActionButtonProps } from 'src/components/Subheader/Subheader.types';
import { getUniqueDataFromCache, HashType, isDataLoaded } from 'src/services/pivotServiceCache';
import { ViewDataState } from 'src/types/Domain';
import {
  MassEdit,
  MassEditConfig,
  MassEditSelectItemData,
  MassEditSubmissionData,
} from 'src/components/MassEdit/MassEdit';
import { Action } from 'redux';

import { executeCalculation } from 'src/utils/LibraryUtils/MathUtils';
import { getLocalConfig } from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.utils';
import { FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { GranularEditPayloadItem } from 'src/dao/pivotClient';
import * as globalMath from 'mathjs';
import { DaysRangeListResponse } from 'src/types/Scope';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import {
  selectTabbedFlowSheetDataLoaded,
  selectTabbedFlowSheetHasMassEditViewDefn,
  selectFlowSheetHasMassEditModel,
  selectTabbedFlowSheetMassEditData,
  selectTabbedFlowSheetViewDataStates,
  selectTabbedFlowSheetMassEditDataLoaded,
} from './FlowSheetByStyle.selectors';
import { WorklistFlowSheetOwnProps } from 'configuration-schemas/ownProps';

type TabbedFlowSheetProps = FlowSheetGridProps & {
  subheaderSummary?: string;
  overTimeData?: TreePivot;
  massEditData: TreePivot;
  allowFrom: string;
  allowTo: string;
  rangeList: DaysRangeListResponse;
  isMassEditDataLoaded: boolean;
  showFlowStatus: boolean;
  viewDefns: FlowSheetViewDefns;
  viewDataStateOverTime: ViewDataState;
  subheaderErrorText?: string;
  viewDataStates: ViewDataState[];
};

export function mapStateToPropsFlowSheet(state: AppState, ownProps: WorklistFlowSheetOwnProps): TabbedFlowSheetProps {
  const { pages, scope, worklist } = state;
  const viewState = pages.assortmentBuild.flowSheet;
  const scopeStart = !isNil(scope.scope.start) ? scope.scope.start : undefined;
  const { viewDefns, viewDataStateOverTime } = viewState;
  const rangeList = scope.rangeList;
  const allowFrom = !isEmpty(rangeList) ? rangeList[0].id : '';
  const allowTo = !isEmpty(rangeList) ? rangeList[rangeList.length - 1].id : '';
  const isMassEditDataLoaded = selectTabbedFlowSheetMassEditDataLoaded(
    state,
    ownProps.massEditModel,
    ownProps.viewDefns,
  );
  const massEditData = selectTabbedFlowSheetMassEditData(state, ownProps.massEditModel);
  const viewDataStates = selectTabbedFlowSheetViewDataStates(state, ownProps.massEditModel);

  const loaded = selectTabbedFlowSheetDataLoaded(state, ownProps.massEditModel);
  const overtimeData = getUniqueDataFromCache(viewState, HashType.flowsheetOverTime)?.tree || [];
  const isLiveDataReady = viewDataStateOverTime === ViewDataState.liveDataReady;
  const groupRowHeight = viewState.viewDefns.grid.main?.groupRowHeight;
  const adornments: AdornmentType[] = get(viewState.viewDefns, 'grid.adornments', []);

  return {
    loaded,
    scopeTimeEntries: scope.timeInfo.entries,
    overTimeData: overtimeData,
    anchorField: scopeStart,
    rowHeight: viewDefns?.grid.main?.rowHeight,
    groupRowHeight,
    editable: isLiveDataReady,
    ...viewState,
    allowFrom,
    itemId: ownProps.topMembers || '',
    allowTo,
    rangeList: scope.daysRangeList,
    isMassEditDataLoaded,
    massEditData,
    showFlowStatus: ownProps.showFlowStatus === false ? false : true,
    gridDefn: viewDefns.grid,
    adornments,
    viewDataStates,
  };
}

export function dispatchToPropsFlowSheet(
  dispatch: AppThunkDispatch,
  ownProps: WorklistFlowSheetOwnProps,
): ComponentTypeGridDispatchProps {
  const dispatchHandlers = {
    onRenderGrid: async () => {
      const { massEditModel, viewDefns } = ownProps;
      // for legacy reasons, the massEditModel will never be required
      const hasMassEditModel = selectFlowSheetHasMassEditModel({} as AppState, massEditModel);
      // when only the massEdit viewDefn is detected, the worklist companion data will be used by default
      const hasMassEditViewDefnConfigured = selectTabbedFlowSheetHasMassEditViewDefn({} as AppState, viewDefns);

      const configResponse = await container.tenantConfigClient.getTenantViewDefnsWithFavorites({
        defnIds: viewDefns,
        appName: ASSORTMENT,
      });

      const massEditConfig = configResponse[1];
      const massEdit = hasMassEditViewDefnConfigured ? (massEditConfig as unknown as MassEditConfig) : undefined;

      const favoritesList = configResponse[viewDefns.length];
      const unmodifiedViewDefn: TenantConfigViewData = configResponse[0];
      const localConfig = getLocalConfig(
        viewDefns[0],
        favoritesList as unknown as FavoriteResponseItem[],
        dispatch,
        unmodifiedViewDefn,
      );

      const configWithFavorites =
        isNil(localConfig) || isNil(localConfig.config) ? unmodifiedViewDefn : localConfig.config;

      dispatch(
        receiveFlowSheetByStyleConfig({
          // lazy change, types are insane
          grid: configWithFavorites as any,
          listSort: {} as TenantConfigViewData,
          subheaderRollUp: {} as TenantConfigViewData,
          list: {} as TenantConfigViewData,
          unmodifiedViewDefn,
          massEdit,
        }),
      );

      // second condition is technically redundant but is here to satisfy typescript
      if (hasMassEditModel && massEditModel) {
        dispatch(fetchMassEditData(massEditModel));
      }
      dispatch(fetchWorklistOverTimeData(ownProps.topMembers, ownProps.dataApi, ownProps.model));
    },
    submitPayload: (payload: GranularEditPayloadItem[]) => {
      return dispatch(submitFlowSheetPayload(payload));
    },
    updateAssortmentPlan: () => {
      dispatch(updateAssortmentPlan('PlanQueue'));
    },
    onUpdateConfig(config: TenantConfigViewData) {
      dispatch(receiveSomeFlowSheetConfig(config as unknown as FlowSheetViewDefns));
    },
    onRefetchOvertimeData: () => {
      dispatch((_dispatch: AppThunkDispatch, _getState: () => AppState): Promise<Action | void> => {
        dispatch(fetchWorklistOverTimeData(ownProps.topMembers, ownProps.dataApi, ownProps.model));
        if (ownProps.massEditModel != null) {
          dispatch(fetchMassEditData(ownProps.massEditModel));
        }
        return Promise.resolve();
      });
    },
  };

  return dispatchHandlers;
}

const FlowSheetGridWithSubheader = (props: TabbedFlowSheetProps) => {
  const {
    allowFrom,
    allowTo,
    anchorField,
    rangeList,
    isMassEditDataLoaded,
    viewDefns,
    showFlowStatus,
    showUndoBtn,
    subheaderSummary,
    viewDataStates,
  } = props;

  const handleMassEditSubmission = async (submission: MassEditSubmissionData) => {
    const { selectedModifier, selectedItems, selectedWeekList, modifierValue } = submission;

    if (!isNil(selectedModifier) && !isNil(selectedWeekList) && !isNil(viewDefns) && !isNil(viewDefns.massEdit)) {
      const massEditConf = viewDefns.massEdit;
      const configViewItems = MassEdit.getConfigViewItems(massEditConf);
      const selectedModConf = configViewItems[0].modifierTypes.find(
        (modType) => modType.dataIndex === selectedModifier,
      );
      // no null submission data expected, except for modifier value

      const dataPiv = await serviceContainer.pivotService.getFlowSheetByIndex(
        selectedItems.map((i) => i.value),
        selectedWeekList[0],
        selectedWeekList[selectedWeekList.length - 1],
      );
      const data = dataPiv.tree;
      const filteredItems = filter(data, (item) => {
        return findIndex(selectedItems, ['value', item.id]) >= 0 && indexOf(selectedWeekList, item.time) >= 0;
      });
      const updatedWeekData = map(filteredItems, (item) => {
        // we shouldn't run calculation for "set value to empty" setups, so nil modifier values should skip calculation
        if (selectedModConf && selectedModConf.calculation && !isNil(modifierValue)) {
          const newValue = executeCalculation(globalMath, selectedModConf.calculation, (key) => {
            return {
              rowNodeFound: true,
              data: key === '$editValue' ? modifierValue : get(item, key, null),
            };
          });
          return {
            coordinates: {
              product: item.id,
              time: item.time,
              location: item.channel,
            },
            [selectedModifier]: newValue,
          };
        } else {
          return {
            coordinates: {
              product: item.id,
              time: item.time,
              location: item.channel,
            },
            [selectedModifier]: modifierValue,
          };
        }
      });

      if (props.submitPayload) {
        await props.submitPayload(updatedWeekData, true);
      }
      if (props.onRefetchOvertimeData) {
        props.onRefetchOvertimeData();
      }
    }
  };
  const getEditableItems = (): (MassEditSelectItemData & { time: string; weekadjslsu: number })[] | undefined => {
    const { viewDefns, massEditData } = props;
    const { massEdit: massEditConfig } = viewDefns;
    const configViewItems = MassEdit.getConfigViewItems(massEditConfig);
    const itemsDataIndex = !isEmpty(configViewItems) ? configViewItems[1].dataIndex : '';
    const editableItems =
      massEditData &&
      massEditData.map((dataItem) => {
        const label = `${dataItem['name']}-${dataItem[itemsDataIndex]}`;
        const weekadjslsu = dataItem['a_unc_sls_u'];
        return {
          value: dataItem['id'],
          label,
          weekadjslsu,
          time: dataItem['time'],
        };
      });

    return editableItems;
  };

  const onUpdateConfig = (config: TenantConfigViewData) => {
    const { onUpdateConfig } = props;
    if (onUpdateConfig) {
      onUpdateConfig(config);
    }
  };

  const generateExtraActionButtonProps = (): LegacySubheaderActionButtonProps => {
    const massEdit = isNil(viewDefns.massEdit)
      ? undefined
      : {
          config: viewDefns.massEdit,
          allowFrom,
          allowTo,
          scopeStart: anchorField || '',
          rangeList,
          editableItems: uniqBy(getEditableItems(), 'value'),
          title: 'Mass Copy Flowsheet Data',
          handleSubmit: handleMassEditSubmission,
          dataLoading: !isMassEditDataLoaded,
        };

    return {
      massEdit,
    };
  };

  const viewConfigurator = viewDefns &&
    viewDefns.unmodifiedViewDefn && {
      viewConfig: viewDefns.grid,
      unmodifiedViewDefn: viewDefns.unmodifiedViewDefn,
      showPinCheckboxForGrid: false,
      updateConfig: onUpdateConfig,
    };

  const extraActionButtons = generateExtraActionButtonProps();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Subheader
        title={''}
        errorCondition={props.subheaderErrorText ? [props.subheaderErrorText] : [ASSORTMENT_BUILD_FILTER_WARNING]}
        // search is rendered in the companion view already
        showSearch={false}
        showFlowStatus={showFlowStatus}
        summary={subheaderSummary}
        // @ts-ignore
        viewConfigurator={viewConfigurator}
        extraLegacyActionButtons={extraActionButtons}
        showUndoBtn={showUndoBtn}
        viewDataState={viewDataStates}
      />
      <div style={{ flex: 1 }}>
        <FlowSheetGrid {...props} />
      </div>
    </div>
  );
};

export const TabbedFlowSheetGrid = connect(
  mapStateToPropsFlowSheet,
  dispatchToPropsFlowSheet,
)(makeScopeAndFilterSensitive(makePopoverSensitive(FlowSheetGridWithSubheader)));
